
import { AnalysisResult, TestResult, SeverityMatrix } from "../types"; 
import { runWorkerTask } from "./analysisService";
import { getSharedAudioContext } from "./memoryService";
import { APP_CONFIG } from "./config";
import { runStressSuite, StressResult } from "./stressSuite";
import { recomputeScoreWithNewLevel, computeScore } from "./scoreService";
import { getLastAnalysisRawPayload, getLastHarmonyOverride, getLastResult } from "./analysisCache";
import { computeFullAnalysis } from "./dspMath";
import { hasRequiredMetrics } from "../policy/uiDataPolicy";
import { REQUIRED_DIAGNOSTIC_IDS, validateAuditV2_1, CONTRACT_VERSION } from "./auditContract";
import { buildRepairMap } from "./repairMapBuilder";
import { GLOBAL_SEVERITY_MATRIX } from "./severityEngine";

export function generateLevelSweepSummary(analysis: AnalysisResult, customMatrix?: SeverityMatrix): string {
    const sessionId = analysis.audit_trail.scoring_ruleset_id.replace("GSC_SESSION_", "");
    const rawPayload = getLastAnalysisRawPayload(sessionId);
    if (!rawPayload) return "ERROR: NO_RAW_PAYLOAD_IN_CACHE";

    const harmonyOverride = getLastHarmonyOverride(sessionId);
    let summary = `GSC NEURAL SWEEP REPORT - ${new Date().toLocaleTimeString()}\n`;
    summary += `Analysis Hash: ${analysis.audit_trail.analysis_hash.slice(0, 16)}...\n`;
    summary += `Mode: ${customMatrix ? 'PREVIEW (Unsaved Sliders)' : 'CURRENT (Active Profile)'}\n`;
    summary += `-------------------------------------------\n`;

    const matrixToUse = customMatrix || GLOBAL_SEVERITY_MATRIX;

    for (let l = 1; l <= 8; l++) {
        // Injection de la matrice spécifique pour le calcul simulé
        const profile = matrixToUse[l];
        const report = recomputeScoreWithNewLevel(rawPayload, l, harmonyOverride ?? undefined);
        
        const score = report.score !== null ? `${report.score}/100` : "INVALID";
        const status = report.status === 'VALID' ? 'OK' : 'FAIL';
        
        summary += `L${l} [${profile.label.padEnd(15)}]: ${score.padStart(7)} | Status: ${status}\n`;
        if (report.reasons && report.reasons.length > 0) {
            summary += `   > Penalty: ${report.reasons[0].label} (-${report.reasons[0].impact}pts)\n`;
        }
    }
    summary += `-------------------------------------------\n`;
    summary += `Report auto-copied to clipboard.`;
    return summary;
}

export async function runFullDiagnostics(): Promise<TestResult[]> {
    const results: TestResult[] = [];
    const ctx = await getSharedAudioContext();
    results.push({ 
        id: REQUIRED_DIAGNOSTIC_IDS[0], 
        name: "Hardware Clock Integrity", 
        category: "HARDWARE", 
        passed: ctx.state !== 'closed', 
        value: `${ctx.sampleRate}Hz`, 
        expected: "44100Hz", 
        details: `State: ${ctx.state}` 
    });

    try {
        const buf = new Float32Array(1024).fill(0.1);
        const [resMain, resWorker] = await Promise.all([
            computeFullAnalysis({ stuData: buf, refData: null, sampleRate: ctx.sampleRate }),
            runWorkerTask<any>('ANALYZE_FULL', { stuData: buf.buffer, sampleRate: ctx.sampleRate })
        ]);
        const diff = Math.abs(resMain.rms_db - resWorker.rms_db);
        results.push({
            id: REQUIRED_DIAGNOSTIC_IDS[1],
            name: "Main vs Worker Parity",
            category: "DSP",
            passed: diff < 0.0001,
            value: `Diff: ${diff.toFixed(6)}dB`,
            expected: "< 0.0001dB",
            details: "Ensures engine symmetry across threads"
        });
    } catch (e: any) {
        results.push({ id: REQUIRED_DIAGNOSTIC_IDS[1], name: "Main vs Worker Parity", category: "DSP", passed: false, value: "CRASH", expected: "PASS", details: e.message });
    }

    const lastResult = getLastResult();
    let contractOk = false;
    let details = "Aucune session en cache.";
    
    if (lastResult) {
        const hasRhythm = (lastResult.rhythm?.stuEnvelope?.length || 0) > 10;
        const hasHarmony = (lastResult.harmony?.gridValidation?.length || 0) > 0;
        const hasFingerprint = (lastResult.tone?.mfccData?.length || 0) > 0;
        const hasTimingMatch = (lastResult.session?.metrics?.debug?.timingDeviationMs?.length || 0) > 0;
        
        contractOk = hasRhythm && hasHarmony && hasFingerprint && hasTimingMatch;
        details = `Rhy:${hasRhythm} Harm:${hasHarmony} Fing:${hasFingerprint} Match:${hasTimingMatch}`;
    }

    results.push({
        id: REQUIRED_DIAGNOSTIC_IDS[2],
        name: "UI Data Contract (Real-time)",
        category: "INTEGRITY",
        passed: contractOk,
        value: contractOk ? "VALID" : "INVALID",
        expected: "VALID",
        details: details
    });

    return results;
}

export async function runFullStressDiagnostics(): Promise<{ diagnostics: TestResult[], stress: StressResult[] }> {
    const diagnostics = await runFullDiagnostics();
    const stress = await runStressSuite('full');
    return { diagnostics, stress };
}

export function exportSystemAuditReport(results: TestResult[], stress: StressResult[], lastResult: AnalysisResult | null) {
    let sanitizedSession = null;
    if (lastResult) {
        try {
            sanitizedSession = {
                score: lastResult.session.score,
                metrics: lastResult.session.metrics,
                audit: lastResult.audit_trail
            };
        } catch (e) {
            sanitizedSession = "SERIALIZATION_ERROR";
        }
    }

    const report: any = {
        meta: { 
            contract_version: CONTRACT_VERSION,
            timestamp: new Date().toISOString(), 
            engine: APP_CONFIG.ENGINE_VERSION, 
            ua: navigator.userAgent,
            stress_count: stress.length,
            diag_count: results.length
        },
        diagnostics: results,
        stress: stress,
        last_session: sanitizedSession || "NULL"
    };

    const validation = validateAuditV2_1(report);
    report.meta.valid = validation.valid;
    report.meta.tier_status = validation.tierPasses;
    report.repair_map = buildRepairMap(report);

    try {
        const jsonContent = JSON.stringify(report, null, 2);
        const blob = new Blob([jsonContent], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const filename = `GSC_AUDIT_${validation.valid ? 'PASSED' : 'DEBUG'}_${Date.now()}.json`;
        
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 100);
    } catch (err) {
        console.error("CRITICAL_EXPORT_FAILURE", err);
        alert("Erreur lors de la génération du JSON.");
    }
}
